import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isV2PatientVersion } from '@odo/shared-ui';
import { useRefinementApprovalPatient } from '../../../../hooks/useRefinementApprovalPatient';
import { useRecentActions } from '../../../../contexts/RecentActionsContext';
import { usePatientMetadata } from '../../../../providers/PatientMetadataProvider';
import { useToast } from '../../../../providers/ToastProvider';
import {
    approveRefinementDesign,
    requestRefinementDesignRevision
} from '../../../../services/patient/refinement';
import { fetchRdiVersions } from '../../../../services/patient/rdi';
import { fetchLatestEventByType } from '../../../../services/patient/timeline';
import { PatientHeaderCard } from '../PatientApprove/PatientHeaderCard';
import { ReasonCaptureModal } from '../PatientApprove/ReasonCaptureModal';
import { ClarificationHistory } from '../../../../components/clarifications/ClarificationHistory';
import { PatientActionPage } from '../shared/PatientActionPage';
import { VersionedPdfPane } from '../shared/VersionedPdfPane';
import { RefinementApprovalFooter } from './RefinementApprovalFooter';
import { GuidanceCard } from './GuidanceCard';
import { buildRefinementGuidanceSteps } from './refinementGuidanceSteps';
import { INTERNAL_STATUS_PENDING_REFINEMENT_APPROVAL } from '../../../../utils/constants';

const PatientRefinementApproval: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: patient, isLoading } = useRefinementApprovalPatient();
    const { getProfilePictureUrl } = usePatientMetadata();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const [modalOpen, setModalOpen] = useState(false);
    // Tracked separately: the footer's primary "Approve design" spinner should
    // only reflect this action, not a revision request submitted from the modal.
    const [approving, setApproving] = useState(false);
    const [requestingRevision, setRequestingRevision] = useState(false);
    const isBusy = approving || requestingRevision;

    const patientId = patient?.id ?? null;
    const patientName = useMemo(() => patient?.name ?? '', [patient]);
    const profilePicUrl = patientId ? getProfilePictureUrl(patientId) : null;

    const canRequestRevision =
        patient?.internalCaseStatus === INTERNAL_STATUS_PENDING_REFINEMENT_APPROVAL;

    const { data: rdiVersions } = useQuery({
        queryKey: ['patient', patientId, 'rdi-versions'],
        queryFn: () => fetchRdiVersions(patientId!),
        enabled: !!patientId,
        staleTime: 60_000
    });

    const { data: latestRefinementTrigger } = useQuery({
        queryKey: ['patient', patientId, 'refinement-trigger', 'latest'],
        queryFn: () => fetchLatestEventByType(patientId!, 'refinement_trigger'),
        enabled: !!patientId,
        staleTime: 60_000
    });

    // Scopes ClarificationHistory to the current refinement round: a
    // refinement_trigger event marks the start of each new design cycle.
    const clarificationsSince = latestRefinementTrigger?.createdAt ?? null;

    const guidanceSteps = useMemo(
        () =>
            buildRefinementGuidanceSteps({
                manufacturer: patient?.manufacturer ?? null,
                manufacturerLink: patient?.manufacturerLink ?? null,
                hasRdi: (rdiVersions?.length ?? 0) > 0,
                canRequestRevision
            }),
        [patient?.manufacturer, patient?.manufacturerLink, rdiVersions, canRequestRevision]
    );

    const invalidate = (): void => {
        if (!patientId) return;
        queryClient.invalidateQueries({ queryKey: ['clarifications', patientId] });
        queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
        queryClient.invalidateQueries({ queryKey: ['patients'] });
    };

    const handleApprove = async (): Promise<void> => {
        if (!patientId) return;
        setApproving(true);
        try {
            await approveRefinementDesign(patientId);
            invalidate();
            markActionCompleted(patientId, 'Review design');
            showToast('Refinement design approved', 'success');
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to approve design';
            showToast(message, 'error');
        } finally {
            setApproving(false);
        }
    };

    const handleRequestRevision = async (note: string): Promise<void> => {
        if (!patientId) return;
        setRequestingRevision(true);
        try {
            await requestRefinementDesignRevision(patientId, note);
            invalidate();
            markActionCompleted(patientId, 'Review design');
            showToast('Revision request sent to ortho team', 'success');
            setModalOpen(false);
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to send revision request';
            showToast(message, 'error');
        } finally {
            setRequestingRevision(false);
        }
    };

    return (
        <PatientActionPage
            patientId={patientId}
            isLoading={isLoading}
            patientName={patientName}
            breadcrumbTitle="Review design"
            tabIdPrefix="refinement-approval"
            redirectToOverview={isV2PatientVersion(patient?.version)}
            leftPane={
                <VersionedPdfPane
                    patientId={patientId}
                    versions={rdiVersions ?? []}
                    docLabel="RDI"
                />
            }
            caseContent={
                <>
                    <PatientHeaderCard
                        name={patientName || null}
                        email={patient?.email ?? null}
                        phone={patient?.phone ?? null}
                        dateOfBirth={patient?.dateOfBirth ?? null}
                        profilePicUrl={profilePicUrl}
                        caseType={patient?.caseType ?? null}
                        subCaseType={patient?.subCaseType ?? null}
                        manufacturer={patient?.manufacturer ?? null}
                        manufacturerLink={patient?.manufacturerLink ?? null}
                        duration={patient?.treatmentDuration ?? null}
                        compact
                    />
                    <GuidanceCard steps={guidanceSteps} />
                </>
            }
            footer={
                <RefinementApprovalFooter
                    canRequestRevision={canRequestRevision}
                    canApprove={!isBusy && !modalOpen}
                    isSubmitting={approving}
                    onApprove={() => void handleApprove()}
                    onRequestRevision={() => setModalOpen(true)}
                />
            }
        >
            <ReasonCaptureModal
                open={modalOpen}
                tone="amber"
                title="Request a design revision"
                subtitle={patientName || 'Patient'}
                noteLabel="Note for the ortho team"
                notePlaceholder="What would you like changed in this RDI design?"
                helperText="The case will be sent back to the ortho team. "
                submitLabel="Send revision request"
                isSubmitting={requestingRevision}
                onSubmit={(note) => void handleRequestRevision(note)}
                onClose={() => setModalOpen(false)}
                beforeNote={
                    patientId ? (
                        <ClarificationHistory
                            patientId={patientId}
                            since={clarificationsSince}
                            revisionType="request-refinement-design-revision"
                        />
                    ) : undefined
                }
            />
        </PatientActionPage>
    );
};

export default PatientRefinementApproval;
